import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CircularProgress, CssBaseline, Toolbar, useMediaQuery, Button } from '@mui/material';

// project imports
import Breadcrumbs from 'components/ui-component/extended/Breadcrumbs';
import { Header, Sidebar } from 'components/layout/main-layout/sections';
import ReduxModal from 'components/ui-component/modal/ReduxModal';
import ProgressModal from 'components/ui-component/modal/ProgressModal';

import { PERMISSIONS, drawerWidth } from 'store/baseConfig';
import { menuOpen, setUserMenu, setIsMobile, selectSidebarOpened, selectShowUserMenu, selectIsMobile } from 'store/slice/SliceSidebar';
import { closeModal, openModal } from 'store/slice/SliceModal';
import menus from 'menus';

// assets
import { IconChevronRight } from '@tabler/icons-react';
import MainCard from 'components/ui-component/cards/MainCard';
import { Outlet } from 'react-router-dom';
import ErrorBoundary from 'components/layout/ErrorBoundary';
import { Suspense } from 'react';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    
    // Handle left drawer
    const leftDrawerOpened = useSelector(selectSidebarOpened);
    const handleLeftDrawerToggle = () => {
        dispatch(menuOpen(!leftDrawerOpened));
    };

    const { loginState, loginUserInfo } = useSelector((state) => state.auth);
    const { isOpen, modalType, modalProps } = useSelector((state) => state.modal);

    // 리덕스에서 상태 가져오기
    const showUserMenu = useSelector(selectShowUserMenu);
    const isMobile = useSelector(selectIsMobile);

    const userInfo = loginUserInfo.user;
    let perm = null; // 권한 id (관리자:01, 운영자:11, 사용자:21)
    if (userInfo) {
        perm = userInfo.perm_id;
    }

    // 모바일 상태 감지 및 저장
    useEffect(() => {
        dispatch(setIsMobile(matchDownMd));
    }, [matchDownMd, dispatch]);

    // 모바일 접속 시 관리자는 자동으로 사용자 메뉴로 전환
    useEffect(() => {
        if (isMobile && perm === PERMISSIONS.ADMIN) {
            dispatch(setUserMenu(true));
        }
    }, [isMobile, perm, dispatch]);

    const handleCloseModal = () => {
        dispatch(closeModal());
    };

    // Progress modal context for legacy support
    const progressContext = {
        openProgressModal: () => dispatch(openModal({ modalType: 'progress' })),
        closeProgressModal: handleCloseModal
    };

    let menuList;

    switch (perm) {
        case PERMISSIONS.SUPER_ADMIN:
            menuList = menus[PERMISSIONS.SUPER_ADMIN];
            break;
        case PERMISSIONS.ADMIN:
            menuList = showUserMenu ? menus[PERMISSIONS.USER] : menus[PERMISSIONS.ADMIN];
            break;
        case PERMISSIONS.USER:
            menuList = menus[PERMISSIONS.USER];
            break;
        default:
            menuList = menus[PERMISSIONS.ALL];
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: theme.palette.background.default,
                    transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
                }}
            >
                <Toolbar>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <Sidebar 
                drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} 
                drawerToggle={handleLeftDrawerToggle} 
                menuList={menuList}
                isAdmin={perm === PERMISSIONS.ADMIN}
            />

            {/* main content */}
            <ErrorBoundary>
                <Suspense fallback={<CircularProgress></CircularProgress>}>
                    <Main open={leftDrawerOpened}>
                        {/* breadcrumb */}
                        <Breadcrumbs separator={IconChevronRight} navigation={{ items: menuList }} icon title rightAlign />
                        <MainCard>
                            <Outlet context={progressContext} />
                        </MainCard>
                    </Main>
                </Suspense>
            </ErrorBoundary>

            {/* Global Modals */}
            <ReduxModal 
                open={isOpen && modalType === 'basic'} 
                handleClose={handleCloseModal}
                title={modalProps?.title || ''}
                message={modalProps?.message || ''}
                severity={modalProps?.severity || 'info'}
            />
            <ProgressModal 
                open={isOpen && modalType === 'progress'} 
                handleClose={handleCloseModal}
            />

            {/* <Customization /> */}
        </Box>
    );
};

export default MainLayout;
